import { forwardRef, useRef, useMemo } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import {
  MenuListContent,
  MenuListRoot,
  MenuListTrigger,
  Text,
  usePreventScroll,
} from '@myrealtrip/web-ui';

import Show from '~components/Show';
import {
  DEPARTURE_TIME_OPTION_LIST_HEIGHT,
  DEPARTURE_TIME_OPTION_LIST_WIDTH,
  DEPARTURE_TIME_OPTION_CONTAINER_PADDING,
} from '~constants/common';

import TimeOptionList from './TimeOptionList';

interface Props {
  isOutboundSelectorOpen: boolean;
  isInboundSelectOpen: boolean;
  outboundTime?: string | null;
  inboundTime?: string | null;
  onClickOutboundTime: () => void;
  onClickInboundTime: () => void;
  onSelectOutboundTime: (time: string) => () => void;
  onSelectInboundTime: (time: string) => () => void;
}

const MultipleTimeSelector = forwardRef<HTMLUListElement, Props>(
  (
    {
      isOutboundSelectorOpen,
      isInboundSelectOpen,
      outboundTime,
      inboundTime,
      onClickOutboundTime,
      onClickInboundTime,
      onSelectOutboundTime,
      onSelectInboundTime,
    }: Props,
    timeOptionRef,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);

    usePreventScroll({ isDisabled: !(isOutboundSelectorOpen || isInboundSelectOpen) });

    const GAP = 2;
    const positionTop = useMemo(() => {
      return (
        -(containerRef.current?.clientHeight || 0) +
        DEPARTURE_TIME_OPTION_CONTAINER_PADDING / 2 -
        GAP -
        DEPARTURE_TIME_OPTION_LIST_HEIGHT
      );
    }, [isOutboundSelectorOpen, isInboundSelectOpen]);

    return (
      <Container ref={containerRef}>
        <MenuListRoot isOpen={isOutboundSelectorOpen} positionLeft={-12} positionTop={positionTop}>
          <TimeOption position="left" onClick={onClickOutboundTime}>
            <StyledMenuListTrigger>
              <TextWrapper>
                <Label typography="captionNormal12">가는날 시간</Label>
                <SelectedTime typography="paragraphNormal16">{outboundTime} 이후</SelectedTime>
              </TextWrapper>
            </StyledMenuListTrigger>
          </TimeOption>
          <MenuListContent>
            <Show when={isOutboundSelectorOpen}>
              <TimeOptionList
                selectedTime={outboundTime}
                onSelectTime={onSelectOutboundTime}
                ref={timeOptionRef}
              />
            </Show>
          </MenuListContent>
        </MenuListRoot>
        <Divider />
        <MenuListRoot isOpen={isInboundSelectOpen} positionLeft={-11.5} positionTop={positionTop}>
          <TimeOption position="right" onClick={onClickInboundTime}>
            <StyledMenuListTrigger>
              <TextWrapper>
                <Label typography="captionNormal12">오는날 시간</Label>
                <SelectedTime typography="paragraphNormal16">{inboundTime} 이후</SelectedTime>
              </TextWrapper>
            </StyledMenuListTrigger>
          </TimeOption>
          <MenuListContent>
            <Show when={isInboundSelectOpen}>
              <TimeOptionList
                selectedTime={inboundTime}
                onSelectTime={onSelectInboundTime}
                ref={timeOptionRef}
              />
            </Show>
          </MenuListContent>
        </MenuListRoot>
      </Container>
    );
  },
);

MultipleTimeSelector.displayName = 'MultipleTimeSelector';

export default MultipleTimeSelector;

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  width: 100%;
`;

const Divider = styled.div`
  width: 1px;
  height: 100%;
  background: ${({ theme }) => theme.colors.white};
`;

const TimeOption = styled.div<{ position: 'left' | 'right' }>`
  display: flex;
  background: ${({ theme }) => theme.colors.gray.s60};
  padding: 5.5px 16px;
  cursor: pointer;
  ${({ position }) =>
    position === 'left' &&
    css`
      border-top-left-radius: 16px;
      border-bottom-left-radius: 16px;
    `}
  ${({ position }) =>
    position === 'right' &&
    css`
      border-top-right-radius: 16px;
      border-bottom-right-radius: 16px;
    `}
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  padding: 0 4px;
`;

const Label = styled(Text)`
  color: ${({ theme }) => theme.colors.gray.s500};
`;

const StyledMenuListTrigger = styled(MenuListTrigger)`
  flex-basis: ${DEPARTURE_TIME_OPTION_LIST_WIDTH}px;
`;

const SelectedTime = styled(Text)`
  color: ${({ theme }) => theme.colors.gray.s1000};
`;
